import { addDays, addMonths, addYears, format, getYear, startOfWeek } from 'date-fns';
import da from 'date-fns/locale/da';
import { capitalize } from 'lodash';
import { formatDate } from '../../utilities';
import { CalendarView } from './calendar-types';

const mondayStart = { weekStartsOn: 1 } as const;

export const CALENDAR_VIEWS = {
  timeGridDay: {
    type: 'timeGrid',
    duration: { days: 1 },
    dayHeaderFormat: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    },
  },
  timeGridWeek: {
    type: 'timeGrid',
    dayHeaderFormat: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    },
  },
  listAgendaCurrentPlusMonth: {
    type: 'list',
    visibleRange() {
      const startDate = new Date();
      const endDate = addMonths(startDate, 1);

      return {
        start: startDate,
        end: endDate,
      };
    },
  },
  dayGridMonth: {
    dayHeaderFormat: {
      weekday: 'long',
    },
  },
  multiThreeMonths: {
    type: 'multiMonthYear',
    dayHeaderFormat: {
      weekday: 'long',
    },
    duration: { month: 3 },
  },
  multiOneYear: {
    type: 'multiMonthYear',
    dayHeaderFormat: {
      weekday: 'long',
    },
    duration: { month: 12 },
  },
} as const;

export function getNextStartDate(view: CalendarView, currentStartDate: Date): Date {
  switch (view) {
    case 'day':
      return addDays(currentStartDate, 1);
    case 'week':
      return startOfWeek(addDays(currentStartDate, 7), mondayStart);
    case 'agenda':
      return startOfWeek(addMonths(currentStartDate, 1));
    case 'month':
      return startOfWeek(addMonths(currentStartDate, 1));
    case 'threeMonths':
      return startOfWeek(addMonths(currentStartDate, 1));
    case 'year':
      return startOfWeek(addYears(currentStartDate, 1));
    default:
      return startOfWeek(addDays(currentStartDate, 6));
  }
}

export function getPreviousStartDate(view: CalendarView, currentStartDate: Date): Date {
  switch (view) {
    case 'day':
      return addDays(currentStartDate, -1);
    case 'week':
      return startOfWeek(addDays(currentStartDate, -7), mondayStart);
    case 'agenda':
      return startOfWeek(addMonths(currentStartDate, -1));
    case 'month':
      return startOfWeek(addMonths(currentStartDate, -1));
    case 'threeMonths':
      return startOfWeek(addMonths(currentStartDate, -2));
    case 'year':
      return startOfWeek(addYears(currentStartDate, -1));
    default:
      return startOfWeek(addDays(currentStartDate, -6));
  }
}

export function getHeaderTitle(calendarView: CalendarView, firstDate: Date) {
  switch (calendarView) {
    case 'day':
      return formatDate(firstDate);
    case 'week':
      return `${formatDate(firstDate)} - ${formatDate(addDays(firstDate, 6))}`;
    case 'agenda':
      return `${getMonthName(firstDate)} ${firstDate.getFullYear()}`;
    case 'month':
      return `${getMonthName(firstDate)} ${firstDate.getFullYear()}`;
    case 'threeMonths':
      return `${getMonthName(firstDate)} - ${getMonthName(addMonths(firstDate, 2))}`;
    case 'year':
      return `${getMonthName(firstDate)} ${getYear(firstDate)} - ${getMonthName(addMonths(firstDate, 11))} ${getYear(addMonths(firstDate, 11))}`;
    default:
      return '';
  }
}

export function getCalendarViewDisplay(view: CalendarView) {
  switch (view) {
    case 'day':
      return 'Dag';
    case 'week':
      return 'Uge';
    case 'agenda':
      return 'Agenda';
    case 'month':
      return 'Måned';
    case 'threeMonths':
      return 'Tre måneder';
    case 'year':
      return 'År';
    default:
      return '';
  }
}

function getMonthName(date: Date) {
  return capitalize(format(date, 'LLLL', { locale: da }));
}
